/**
 * DocumentsDashboard Component
 * 
 * Dashboard de documentação - exibe estatísticas, documentos faltantes e prestes a vencer.
 * 
 * Funcionalidades:
 * - Cards com estatísticas de documentos
 * - Lista de documentos faltantes
 * - Lista de documentos prestes a vencer
 * - Filtros por grupo de acesso
 */

import { useState, useEffect } from 'react'
import { AlertTriangle, FileText, FileWarning, Users } from 'lucide-react'
import { documentDashboardService } from '../../../services/documentsApi'
import { DocumentExpiryAlert } from '../../../components/Documents'
import './documentsShell.css'
import './DocumentsDashboard.css'

function DocumentsDashboard() {
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [accessGroups] = useState([])
  const [selectedAccessGroup, setSelectedAccessGroup] = useState('')

  useEffect(() => {
    loadDashboardData()
  }, [selectedAccessGroup])

  const loadDashboardData = async () => {
    setLoading(true)
    try {
      const response = await documentDashboardService.getDashboard()
      
      if (response.success) {
        setDashboardData(response.data)
      }
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error)
    } finally {
      setLoading(false)
    }
  }

  // TODO: Carregar grupos de acesso quando necessário
  // useEffect(() => {
  //   loadAccessGroups()
  // }, [])

  if (loading) {
    return (
      <div className="docs-page">
        <div className="docs-state">Carregando documentação...</div>
      </div>
    )
  }

  if (!dashboardData) {
    return (
      <div className="docs-page">
        <div className="docs-state">Não foi possível carregar o dashboard de documentos.</div>
      </div>
    )
  }

  const stats = dashboardData.statistics || {}

  return (
    <div className="docs-page documents-dashboard">
      <header className="docs-header">
        <div>
          <h1 className="docs-title">
            Documentação <span className="docs-title-highlight">Geral</span>
          </h1>
          <p className="docs-subtitle">Empresa, usuários, vencimentos e pendências</p>
        </div>
        {accessGroups.length > 0 && (
          <div className="docs-header-actions">
            <label className="docs-select">
              Grupo
              <select
                value={selectedAccessGroup}
                onChange={(e) => setSelectedAccessGroup(e.target.value)}
              >
                <option value="">Todos</option>
                {accessGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
      </header>

      <section className="docs-kpi-grid">
        <article className="docs-kpi">
          <div className="docs-kpi-icon docs-kpi-icon--green">
            <FileText size={24} />
          </div>
          <p className="docs-kpi-label">Documentos da empresa</p>
          <p className="docs-kpi-value">{stats.companyDocuments || 0}</p>
        </article>
        <article className="docs-kpi">
          <div className="docs-kpi-icon docs-kpi-icon--blue">
            <Users size={24} />
          </div>
          <p className="docs-kpi-label">Documentos de usuários</p>
          <p className="docs-kpi-value">{stats.userDocuments || 0}</p>
        </article>
        <article className="docs-kpi">
          <div className="docs-kpi-icon docs-kpi-icon--amber">
            <AlertTriangle size={24} />
          </div>
          <p className="docs-kpi-label">Vencendo</p>
          <p className="docs-kpi-value">{stats.expiringSoon || 0}</p>
          <p className="docs-kpi-hint">Próximos 30 dias</p>
        </article>
        <article className="docs-kpi">
          <div className="docs-kpi-icon docs-kpi-icon--rose">
            <FileWarning size={24} />
          </div>
          <p className="docs-kpi-label">Faltantes</p>
          <p className="docs-kpi-value">{stats.missingDocuments || 0}</p>
          <p className="docs-kpi-hint">Obrigatórios não enviados</p>
        </article>
      </section>

      {dashboardData.expiringDocuments && dashboardData.expiringDocuments.length > 0 && (
        <section>
          <h2 className="docs-section-title">Prestes a vencer</h2>
          <DocumentExpiryAlert
            documents={dashboardData.expiringDocuments}
            onDocumentClick={() => {}}
          />
        </section>
      )}

      {dashboardData.missingUserDocuments && dashboardData.missingUserDocuments.length > 0 && (
        <section className="docs-panel">
          <h2>Documentos faltantes</h2>
          {dashboardData.missingUserDocuments.map((item, index) => (
            <div className="docs-row" key={index}>
              <div>
                <strong>{item.user_name || 'Usuário'}</strong>
                <span>{item.category_name}</span>
              </div>
              <span className="docs-badge docs-badge--rose">Faltante</span>
            </div>
          ))}
        </section>
      )}

      {(!dashboardData.expiringDocuments || dashboardData.expiringDocuments.length === 0) &&
        (!dashboardData.missingUserDocuments || dashboardData.missingUserDocuments.length === 0) && (
        <section className="docs-panel">
          <p className="docs-empty">Todos os documentos estão em dia.</p>
        </section>
      )}
    </div>
  )
}

export default DocumentsDashboard

